import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, CheckCircle, Trash2, Plus, Package, Ruler, RefreshCw } from 'lucide-react';
import { itemBinSizeGroupService } from '../../../../../services/itemBinSizeGroupService';
import { ItemBinSizeGroupDto, GroupBinSizeLineRequest, ItemBinSizeGroupItemDto } from '../../../../../types/itemBinSizeGroup';
import { ItemBinSizeDto } from '../../../../../types/itemBinSize';

interface SelectedRows {
    [key: string]: boolean;
}

interface ItemBinSizeGroupSidePanelProps {
    selectedRows: SelectedRows;
    data: ItemBinSizeGroupDto[];
    onClose: () => void;
    onSave: () => void;
}

export const ItemBinSizeGroupSidePanel: React.FC<ItemBinSizeGroupSidePanelProps> = ({
    selectedRows,
    data,
    onClose,
    onSave
}) => {
    const [activeTab, setActiveTab] = useState<'bin-sizes' | 'items' | 'delete'>('bin-sizes');
    const [binSizeLines, setBinSizeLines] = useState<ItemBinSizeDto[]>([]);
    const [items, setItems] = useState<ItemBinSizeGroupItemDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState<string>('');
    const [newItemNo, setNewItemNo] = useState('');
    const [editedLines, setEditedLines] = useState<GroupBinSizeLineRequest[]>([]);
    const [newLine, setNewLine] = useState<GroupBinSizeLineRequest>({ binSizeNo: 0, maxQuantity: 0, putAwayPriorityNo: 100 });

    const selectedData = data.filter(item => selectedRows[item.id.toString()]);
    const selectedGroup = selectedData.length === 1 ? selectedData[0] : null;

    useEffect(() => {
        if (selectedGroup) {
            loadGroupDetails();
        }
    }, [selectedGroup?.id]);

    const loadGroupDetails = async () => {
        if (!selectedGroup) return;
        try {
            setLoading(true);
            setError('');
            const [binSizes, groupItems] = await Promise.all([
                itemBinSizeGroupService.getBinSizeLines(selectedGroup.companyNo, selectedGroup.itemBinSizeGroupID),
                itemBinSizeGroupService.getItems(selectedGroup.companyNo, selectedGroup.itemBinSizeGroupID)
            ]);
            setBinSizeLines(binSizes);
            setEditedLines(binSizes.map(l => ({
                binSizeNo: l.binSizeNo,
                maxQuantity: l.maxQuantity,
                putAwayPriorityNo: l.putAwayPriorityNo
            })));
            setItems(groupItems);
        } catch (err: any) {
            setError(err.message || 'Feil ved lasting av gruppedetaljer');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveBinSizes = async () => {
        if (!selectedGroup) return;
        try {
            setIsSaving(true);
            setError('');
            await itemBinSizeGroupService.updateBinSizeLines(
                selectedGroup.companyNo,
                selectedGroup.itemBinSizeGroupID,
                editedLines.filter(l => l.binSizeNo > 0)
            );
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSave();
            }, 1500);
        } catch (err: any) {
            setError(err.message || 'Feil ved lagring av størrelser');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddLine = () => {
        if (newLine.binSizeNo <= 0) return;
        setEditedLines(prev => [...prev, { ...newLine }]);
        setNewLine({ binSizeNo: 0, maxQuantity: 0, putAwayPriorityNo: 100 });
    };

    const handleRemoveLine = (index: number) => {
        setEditedLines(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpdateLine = (index: number, field: keyof GroupBinSizeLineRequest, value: number) => {
        setEditedLines(prev => prev.map((line, i) =>
            i === index ? { ...line, [field]: value } : line
        ));
    };

    const handleAddItem = async () => {
        if (!selectedGroup || !newItemNo.trim()) return;
        try {
            setLoading(true);
            setError('');
            await itemBinSizeGroupService.assignItems(
                selectedGroup.companyNo,
                selectedGroup.itemBinSizeGroupID,
                { itemNos: [newItemNo.trim()] }
            );
            setNewItemNo('');
            await loadGroupDetails();
        } catch (err: any) {
            setError(err.message || 'Feil ved tilordning av vare');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveItem = async (itemNo: string) => {
        if (!selectedGroup) return;
        try {
            setLoading(true);
            setError('');
            await itemBinSizeGroupService.removeItem(
                selectedGroup.companyNo,
                selectedGroup.itemBinSizeGroupID,
                itemNo
            );
            await loadGroupDetails();
        } catch (err: any) {
            setError(err.message || 'Feil ved fjerning av vare');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteGroup = async () => {
        if (!selectedGroup) return;
        try {
            setIsDeleting(true);
            setError('');
            await itemBinSizeGroupService.delete(
                selectedGroup.companyNo,
                selectedGroup.itemBinSizeGroupID
            );
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSave();
            }, 1500);
        } catch (err: any) {
            setError(err.message || 'Feil ved sletting av gruppe');
        } finally {
            setIsDeleting(false);
        }
    };

    if (selectedData.length > 1) {
        return (
            <div className="fixed inset-0 z-50 overflow-hidden">
                <div className="absolute inset-0 bg-black bg-opacity-25 transition-opacity" onClick={onClose} />
                <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl border-l border-gray-200 flex flex-col">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Gruppedetaljer</h2>
                            <p className="text-sm text-gray-600">Velg kun 1 gruppe for detaljer</p>
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="flex-1 flex items-center justify-center p-8 text-center text-gray-500">
                        <div>
                            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p>Du har valgt {selectedData.length} grupper.</p>
                            <p className="text-sm mt-1">Velg kun 1 gruppe for å se og redigere detaljer.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!selectedGroup) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-25 transition-opacity" onClick={onClose} />

            <div className="absolute right-0 top-0 h-full w-[480px] bg-white shadow-xl border-l border-gray-200 flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            Gruppe {selectedGroup.itemBinSizeGroupID}
                        </h2>
                        <p className="text-sm text-gray-600">{selectedGroup.description1}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('bin-sizes')}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                            activeTab === 'bin-sizes'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <Ruler className="w-3.5 h-3.5" />
                        Størrelser ({editedLines.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('items')}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                            activeTab === 'items'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <Package className="w-3.5 h-3.5" />
                        Varer ({items.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('delete')}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                            activeTab === 'delete'
                                ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                        Slett
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {loading && (
                        <div className="flex items-center justify-center py-4">
                            <RefreshCw className="w-5 h-5 animate-spin text-blue-600 mr-2" />
                            <span className="text-sm text-gray-600">Laster...</span>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'bin-sizes' && !loading && (
                        <>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">Lokasjonsstørrelser for gruppe</p>
                                    <p>Definer hvilke lokasjonsstørrelser og kapasiteter som gjelder for denne gruppen.</p>
                                </div>
                            </div>

                            <div className="space-y-2">
                                {editedLines.map((line, index) => (
                                    <div key={index} className="border border-gray-200 rounded-lg p-3 bg-white">
                                        <div className="grid grid-cols-3 gap-2">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Størrelse nr</label>
                                                <input
                                                    type="number"
                                                    value={line.binSizeNo}
                                                    onChange={(e) => handleUpdateLine(index, 'binSizeNo', parseInt(e.target.value) || 0)}
                                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Maks antall</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={line.maxQuantity}
                                                    onChange={(e) => handleUpdateLine(index, 'maxQuantity', parseFloat(e.target.value) || 0)}
                                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                                />
                                            </div>
                                            <div className="flex items-end gap-1">
                                                <div className="flex-1">
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Prioritet</label>
                                                    <input
                                                        type="number"
                                                        value={line.putAwayPriorityNo}
                                                        onChange={(e) => handleUpdateLine(index, 'putAwayPriorityNo', parseInt(e.target.value) || 0)}
                                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                                    />
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveLine(index)}
                                                    className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors mb-0.5"
                                                    title="Fjern linje"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="border border-dashed border-gray-300 rounded-lg p-3 bg-gray-50">
                                <p className="text-xs font-medium text-gray-500 mb-2">Legg til ny linje</p>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <input
                                            type="number"
                                            value={newLine.binSizeNo || ''}
                                            onChange={(e) => setNewLine(prev => ({ ...prev, binSizeNo: parseInt(e.target.value) || 0 }))}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                            placeholder="Str. nr"
                                        />
                                    </div>
                                    <div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={newLine.maxQuantity || ''}
                                            onChange={(e) => setNewLine(prev => ({ ...prev, maxQuantity: parseFloat(e.target.value) || 0 }))}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                            placeholder="Maks ant."
                                        />
                                    </div>
                                    <div className="flex items-end gap-1">
                                        <input
                                            type="number"
                                            value={newLine.putAwayPriorityNo || ''}
                                            onChange={(e) => setNewLine(prev => ({ ...prev, putAwayPriorityNo: parseInt(e.target.value) || 0 }))}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                            placeholder="Prioritet"
                                        />
                                        <button
                                            onClick={handleAddLine}
                                            disabled={newLine.binSizeNo <= 0}
                                            className={`p-1.5 rounded transition-colors ${
                                                newLine.binSizeNo > 0
                                                    ? 'text-green-600 hover:text-green-800 hover:bg-green-50'
                                                    : 'text-gray-300 cursor-not-allowed'
                                            }`}
                                            title="Legg til"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'items' && !loading && (
                        <>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">Tilordnede varer</p>
                                    <p>Varer som bruker denne gruppens lokasjonsstørrelse-konfigurasjon.</p>
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newItemNo}
                                    onChange={(e) => setNewItemNo(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Varenummer..."
                                />
                                <button
                                    onClick={handleAddItem}
                                    disabled={!newItemNo.trim()}
                                    className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        newItemNo.trim()
                                            ? 'bg-green-600 text-white hover:bg-green-700'
                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    <Plus className="w-4 h-4" />
                                    Legg til
                                </button>
                            </div>

                            <div className="space-y-1 max-h-[400px] overflow-y-auto">
                                {items.length === 0 ? (
                                    <div className="text-center py-6 text-gray-400">
                                        <Package className="w-8 h-8 mx-auto mb-2" />
                                        <p className="text-sm">Ingen varer tilordnet denne gruppen</p>
                                    </div>
                                ) : (
                                    items.map(item => (
                                        <div
                                            key={item.itemNo}
                                            className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-gray-900 truncate">{item.itemNo}</p>
                                                {item.description1 && (
                                                    <p className="text-xs text-gray-500 truncate">{item.description1}</p>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => handleRemoveItem(item.itemNo)}
                                                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0 ml-2"
                                                title="Fjern fra gruppe"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}

                    {activeTab === 'delete' && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-red-800">
                                    <p className="font-medium mb-1">Slett gruppe</p>
                                    <p>
                                        Du er i ferd med å slette gruppe {selectedGroup.itemBinSizeGroupID} ({selectedGroup.description1}).
                                        {items.length > 0 && (
                                            <span className="block mt-1 font-medium">
                                                Gruppen har {items.length} tilordnede varer som vil miste sin gruppetilhørighet.
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="flex items-center justify-between gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Lukk
                        </button>

                        {activeTab === 'bin-sizes' && (
                            <button
                                onClick={handleSaveBinSizes}
                                disabled={isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Lagrer...
                                    </>
                                ) : showSuccess ? (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        Lagret!
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        Lagre størrelser
                                    </>
                                )}
                            </button>
                        )}

                        {activeTab === 'delete' && (
                            <button
                                onClick={handleDeleteGroup}
                                disabled={isDeleting}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isDeleting
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-red-600 text-white hover:bg-red-700'
                                }`}
                            >
                                {isDeleting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Sletter...
                                    </>
                                ) : showSuccess ? (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        Slettet!
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        Slett gruppe
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
